import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AttestationKind } from "@graftus/schema";
import { cn } from "@/lib/utils";
import { focusRing } from "./buttons";

interface AttestationListProps {
  required: readonly AttestationKind[];
  attested: Partial<Record<AttestationKind, boolean>>;
  onChange: (kind: AttestationKind, checked: boolean) => void;
}

/** "Innan du skickar": one checkbox per required confirmation; sending stays blocked until all are ticked. */
export function AttestationList({ required, attested, onChange }: AttestationListProps) {
  const { t } = useTranslation("onboarding");
  if (required.length === 0) return null;
  return (
    <fieldset className="rounded-g-lg border border-g-line bg-g-bg-card p-4">
      <legend className="px-1 text-[13px] font-semibold text-g-ink-700">{t("invite.before.title")}</legend>
      <div className="flex flex-col gap-3">
        {required.map((kind) => {
          const checked = attested[kind] === true;
          const id = `attest-${kind}`;
          return (
            <label
              key={kind}
              htmlFor={id}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-g-md border p-3 transition-colors",
                checked ? "border-g-success bg-g-success-tint" : "border-g-line hover:bg-g-primary-subtle",
              )}
            >
              <span className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                <input
                  id={id}
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onChange(kind, e.currentTarget.checked)}
                  className={cn("peer h-5 w-5 appearance-none rounded-g-xs border border-g-line-strong bg-g-bg-card transition-colors checked:border-g-success checked:bg-g-success", focusRing)}
                />
                <Check className="pointer-events-none absolute h-3.5 w-3.5 text-g-on-primary opacity-0 peer-checked:opacity-100" strokeWidth={3} aria-hidden="true" />
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-[14px] font-semibold text-g-ink-900">{t(`invite.before.${kind}.title`)}</span>
                <span className="text-[12.5px] text-g-ink-500">{t(`invite.before.${kind}.desc`)}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
